import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { XMarkIcon, UserIcon } from '@heroicons/react/24/outline';

interface ImpersonationBarProps {
  /** Full-width pill with "Viewing as"/Exit copy (sidebar expanded,
   * MobileNav) — default. Set false for the collapsed rail's icon-only
   * exit affordance. */
  expanded?: boolean;
}

// Rendered inside the sidebar footer, above the theme toggle (deepcontext-v9kg)
// — a compact accent-tinted pill. Condensed copy (name + Exit); email/role/
// company-access/admin-name live in the title tooltip (deepcontext-x0be).
const ImpersonationBar: React.FC<ImpersonationBarProps> = ({ expanded = true }) => {
  const { impersonation, user, stopImpersonation } = useAuth();

  if (!impersonation?.active) {
    return null;
  }

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
    }
  };

  const details = [
    user?.email,
    user?.role && `Role: ${user.role}`,
    user?.companyId && 'Has company access',
    impersonation.adminName && `Admin: ${impersonation.adminName}`,
  ]
    .filter(Boolean)
    .join(' - ');

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={handleStopImpersonation}
        title={`Viewing as ${user?.name} - ${details}. Click to exit impersonation.`}
        className="flex w-full items-center justify-center rounded-full border border-accent/40 bg-nav-active p-2.5 text-accent-active transition-colors hover:bg-canvas"
      >
        <UserIcon className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div
      title={details}
      className="mb-0.5 flex w-full items-center justify-between gap-2 rounded-full border border-accent/40 bg-nav-active px-3 py-1.5 text-xs text-accent-active shadow-sm"
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <UserIcon className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">
          Viewing as <strong>{user?.name}</strong>
        </span>
      </span>
      <button
        onClick={handleStopImpersonation}
        className="flex flex-shrink-0 items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-accent-active"
      >
        <XMarkIcon className="h-3.5 w-3.5" />
        <span>Exit</span>
      </button>
    </div>
  );
};

export default ImpersonationBar;
